import { z } from 'zod'
import { createdAtSortQueryShape, creatorListQueryShape } from '../list-query/contract.ts'
import { businessLicenseOcrResultSchema, idCardOcrResultSchema, ocrFileSchema } from '../ocr/contract.ts'
import { accountDisplaySchema } from '../profile/contract.ts'

export const licensePreviewSchema = z.object({
  company: businessLicenseOcrResultSchema,
  identity: idCardOcrResultSchema.strip()
})
export type LicensePreview = z.infer<typeof licensePreviewSchema>

type LicenseUpload = { company: File; identity: File }
export type LicenseConfirmInput = LicenseUpload & { expected: LicensePreview; confirmed: 'true' }

export const licenseUploadSchema: z.ZodType<LicenseUpload, FormData> = z
  .instanceof(FormData)
  .transform((data) => ({
    company: data.get('company'),
    identity: data.get('identity')
  }))
  .pipe(z.object({ company: ocrFileSchema, identity: ocrFileSchema }))

export const licenseConfirmSchema: z.ZodType<LicenseConfirmInput, FormData> = z
  .instanceof(FormData)
  .transform((data, ctx) => {
    let expected: unknown
    try {
      expected = JSON.parse(String(data.get('expected')))
    } catch {
      ctx.addIssue({ code: 'custom', message: '请先识别并核对资料' })
      return z.NEVER
    }
    return { company: data.get('company'), identity: data.get('identity'), expected, confirmed: data.get('confirmed') }
  })
  .pipe(
    z.object({
      company: ocrFileSchema,
      identity: ocrFileSchema,
      expected: licensePreviewSchema,
      confirmed: z.literal('true')
    })
  )

export const licenseListSchema = z.object({
  current: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
  id: z.number().int().positive().optional(),
  keyword: z.string().trim().max(100).optional(),
  ...creatorListQueryShape,
  ...createdAtSortQueryShape
})
export type LicenseListInput = z.infer<typeof licenseListSchema>
export const licenseRowSchema = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  unifiedSocialCreditCode: z.string(),
  legalRepresentative: z.string().nullable(),
  identityId: z.number().int().positive(),
  identityName: z.string(),
  maskedIdNumber: z.string(),
  businessLicenseFileId: z.number().nullable(),
  frontFileId: z.number().nullable(),
  creator: accountDisplaySchema,
  createdAt: z.date()
})
export const licenseListOutputSchema = z.object({ data: licenseRowSchema.array(), total: z.number() })
export type LicenseRow = z.infer<typeof licenseRowSchema>

export function licenseValidationMessage(preview: LicensePreview): string | undefined {
  const { company, identity } = preview
  if (!company.name?.trim() || !/^[0-9A-Z]{18}$/.test(company.unifiedSocialCreditCode?.trim().toUpperCase() ?? '')) {
    return '执照企业名称或统一社会信用代码识别不完整，请重新上传清晰图片'
  }
  if (
    !identity.name?.trim() ||
    !/^(?:[0-9]{15}|[0-9]{17}[0-9X])$/.test(identity.idNumber?.trim().toUpperCase() ?? '')
  ) {
    return '身份证姓名或身份证号识别不完整，请上传身份证人像面'
  }
  if (!company.legalRepresentative?.trim()) {
    return '未识别到执照法定代表人，请重新上传'
  }
  if (company.legalRepresentative.trim() !== identity.name.trim()) {
    return '执照法定代表人与身份证姓名不一致，无法关联，请核对后重新上传'
  }
  return undefined
}
